import { useState, useEffect } from 'react';
import { alterationAnalyticsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, Clock, IndianRupee, RefreshCw, Eye, ChevronRight,
  Activity, Target, TrendingUp, Calendar, Info,
} from 'lucide-react';

export default function WorkerReportsPage() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerDetails, setWorkerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Generate month options for last 12 months
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: val, label });
  }

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const res = await alterationAnalyticsAPI.workerSummary(month);
      setWorkers(res.data || []);
    } catch (err) {
      toast.error('Failed to load worker summary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, [month]);

  const handleViewDetails = async (worker) => {
    setSelectedWorker(worker);
    setSheetOpen(true);
    setDetailsLoading(true);
    try {
      const res = await alterationAnalyticsAPI.workerDetails(worker.worker_id, month);
      setWorkerDetails(res.data);
    } catch (err) {
      toast.error('Failed to load worker details');
      console.error(err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Calculate totals
  const totals = workers.reduce((acc, w) => ({
    alterations: acc.alterations + w.alteration_count,
    hours: acc.hours + w.actual_hours,
    cost: acc.cost + w.total_cost,
  }), { alterations: 0, hours: 0, cost: 0 });

  const avgEfficiency = workers.length > 0
    ? workers.reduce((s, w) => s + (w.estimated_hours > 0 ? w.actual_hours / w.estimated_hours : 1), 0) / workers.length
    : 1;

  return (
    <div className="space-y-6 p-6" data-testid="worker-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>
            Worker Remuneration Report
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            Monthly alteration work and earnings by worker
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[180px]" data-testid="month-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchWorkers}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(27,42,74,0.08)' }}>
                <Users className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Active Workers</p>
                <p className="text-xl font-bold" style={{ color: 'var(--brand-navy)' }}>
                  {loading ? '-' : workers.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(197,165,90,0.15)' }}>
                <Activity className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Total Alterations</p>
                <p className="text-xl font-bold" style={{ color: 'var(--brand-navy)' }}>
                  {loading ? '-' : totals.alterations}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Total Hours</p>
                <p className="text-xl font-bold text-blue-600">
                  {loading ? '-' : totals.hours.toFixed(1)}h
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
                <IndianRupee className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Total Remuneration</p>
                <p className="text-xl font-bold text-green-600">
                  {loading ? '-' : `₹${totals.cost.toLocaleString('en-IN')}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workers Table */}
      <Card className="rounded-xl border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Users className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
            Worker Summary
          </CardTitle>
          <CardDescription className="text-xs">
            Click on a worker to view detailed alteration records
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : workers.length === 0 ? (
            <div className="text-center py-8">
              <Info className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--ink-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
                No alteration work recorded for {monthOptions.find(m => m.value === month)?.label}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Worker</TableHead>
                  <TableHead className="text-xs">Role</TableHead>
                  <TableHead className="text-xs text-right">Alterations</TableHead>
                  <TableHead className="text-xs text-right">Est. Hours</TableHead>
                  <TableHead className="text-xs text-right">Actual Hours</TableHead>
                  <TableHead className="text-xs text-center">Efficiency</TableHead>
                  <TableHead className="text-xs text-right">Remuneration</TableHead>
                  <TableHead className="text-xs"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workers.map((worker) => {
                  const efficiency = worker.estimated_hours > 0
                    ? (worker.actual_hours / worker.estimated_hours)
                    : 1;
                  const effColor = efficiency <= 1 ? 'text-green-600' : efficiency <= 1.2 ? 'text-amber-600' : 'text-red-600';
                  
                  return (
                    <TableRow
                      key={worker.worker_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleViewDetails(worker)}
                      data-testid={`worker-row-${worker.worker_id}`}
                    >
                      <TableCell className="font-medium text-sm">
                        {worker.worker_name}
                      </TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="outline" className="text-xs capitalize">
                          {worker.role || 'Worker'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums font-medium">
                        {worker.alteration_count}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {worker.estimated_hours}h
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {worker.actual_hours}h
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs font-bold ${effColor}`}>
                          {(efficiency * 100).toFixed(0)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-bold" style={{ color: '#1E7A5F' }}>
                          ₹{worker.total_cost.toLocaleString('en-IN')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <ChevronRight className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Worker Details Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-[600px] bg-white" data-testid="worker-details-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>
              {selectedWorker?.worker_name}
            </SheetTitle>
            <SheetDescription>
              Alteration details for {monthOptions.find(m => m.value === month)?.label}
            </SheetDescription>
          </SheetHeader>

          {detailsLoading ? (
            <div className="space-y-3 mt-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : workerDetails ? (
            <div className="mt-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Alterations</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--brand-navy)' }}>
                    {workerDetails.summary.total_alterations}
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Efficiency</p>
                  <p className="text-lg font-bold" style={{
                    color: workerDetails.summary.efficiency_ratio <= 1 ? '#16A34A'
                      : workerDetails.summary.efficiency_ratio <= 1.2 ? '#D97706' : '#DC2626'
                  }}>
                    {(workerDetails.summary.efficiency_ratio * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Total Earnings</p>
                  <p className="text-lg font-bold" style={{ color: '#1E7A5F' }}>
                    ₹{workerDetails.summary.total_cost.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Detailed Records */}
              <div>
                <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--brand-navy)' }}>
                  Alteration Records
                </h4>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-2">
                    {workerDetails.alterations.map((alt, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg border border-border/50"
                        data-testid={`alt-record-${idx}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                              {alt.order_number}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                              {alt.garment_type} - {alt.description}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold" style={{ color: '#1E7A5F' }}>
                              ₹{alt.cost.toLocaleString('en-IN')}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                              {alt.actual_hours}h / {alt.estimated_hours}h
                            </p>
                          </div>
                        </div>
                        <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                          {alt.date}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Info className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--ink-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
                No details available
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
